import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  GetProjectVersionStatsByIdParams,
  GetProjectVersionStatsByIdData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface GetProjectVersionStatsByIdState
  extends MergeRequestState<GetProjectVersionStatsByIdData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.getProjectVersionStatsById,
  isFetching: (state: ReduxState) =>
    state.server.getProjectVersionStatsById.isFetching,
  data: (state: ReduxState) => state.server.getProjectVersionStatsById.data,
  error: (state: ReduxState) => state.server.getProjectVersionStatsById.error,
};

const initialState: GetProjectVersionStatsByIdState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/getProjectVersionStatsById';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    {
      params,
      config,
    }: { params: GetProjectVersionStatsByIdParams; config?: ServerApiConfig },
    { rejectWithValue },
  ) =>
    serverApi.getProjectVersionStatsById(params, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerGetProjectVersionStatsById {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const getProjectVersionStatsById: ServerGetProjectVersionStatsById = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
